/**
 * 🎮 GAMIFICATION SERVICE — Serviço de Gamificação
 * 
 * Gerencia toda lógica de pontuação, níveis, transações e regras
 * do sistema de gamificação.
 */

import { supabase } from '../lib/supabaseClient';
import type { AuthorityLevel } from '../components/UserAuthorityBadge';

// ============================================================================
// TIPOS
// ============================================================================

export interface UserPoints {
  id: string;
  user_id: string;
  organization_id: string;
  total_points: number;
  discipline_points: number;
  achievement_points: number;
  team_points: number;
  authority_level: AuthorityLevel;
  current_multiplier: number;
  multiplier_expires_at: string | null;
  kaizens_completed: number;
  projects_led: number;
  team_collaborations: number;
  recovery_plans_opened: number;
  created_at: string;
  updated_at: string;
}

export interface GamificationTransaction {
  id: string;
  user_id: string;
  organization_id: string;
  project_id: string | null;
  kaizen_id: string | null;
  transaction_type: TransactionType;
  points_delta: number;
  multiplier_applied: number;
  final_points: number;
  description: string;
  triggered_by: string;
  is_team_transaction: boolean;
  created_at: string;
}

export type TransactionType =
  | 'kpi_bonus'
  | 'leader_bonus'
  | 'team_bonus'
  | 'discipline_penalty'
  | 'recovery_bonus'
  | 'deadline_multiplier'
  | 'kaizen_completion'
  | 'project_milestone'
  | 'manual_adjustment';

export interface GamificationRules {
  id: string;
  organization_id: string;
  kpi_bonus_leader: number;
  kpi_bonus_team: number;
  solidarity_enabled: boolean;
  andon_penalty_points: number;
  recovery_plan_deadline_hours: number;
  recovery_plan_bonus: number;
  deadline_multiplier_enabled: boolean;
  deadline_multiplier_value: number;
  deadline_compliance_threshold: number;
  multiplier_duration_days: number;
  team_weight_percentage: number;
  bronze_min_points: number;
  silver_min_points: number;
  gold_min_points: number;
  black_belt_min_points: number;
  created_at: string;
  updated_at: string;
}

export interface RecoveryPlan {
  id: string;
  kaizen_id: string;
  project_id: string | null;
  organization_id: string;
  created_by: string;
  team_members: string[];
  issue_description: string;
  root_cause: string | null;
  action_plan: string;
  target_date: string;
  status: 'open' | 'in_progress' | 'completed' | 'failed';
  opened_at: string;
  deadline_at: string;
  resolved_at: string | null;
  penalty_stopped: boolean;
  points_recovered: number;
}

export interface LeaderboardEntry {
  user_id: string;
  organization_id: string;
  total_points: number;
  authority_level: AuthorityLevel;
  kaizens_completed: number;
  projects_led: number;
  team_collaborations: number;
  current_multiplier: number;
  email: string;
  user_name: string;
  rank_position: number;
}

// ============================================================================
// GAMIFICATION SERVICE CLASS
// ============================================================================

export class GamificationService {
  
  // --------------------------------------------------------------------------
  // USER POINTS
  // --------------------------------------------------------------------------
  
  /**
   * Obter pontos de um usuário
   */
  static async getUserPoints(
    userId: string,
    organizationId: string
  ): Promise<UserPoints | null> {
    const { data, error } = await supabase
      .from('user_points')
      .select('*')
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao buscar pontos do usuário:', error);
      return null;
    }

    return data;
  }

  /**
   * Criar ou atualizar pontos de um usuário
   */
  static async upsertUserPoints(
    userId: string,
    organizationId: string,
    updates: Partial<UserPoints>
  ): Promise<UserPoints | null> {
    const { data, error } = await supabase
      .from('user_points')
      .upsert({
        user_id: userId,
        organization_id: organizationId,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar pontos:', error);
      return null;
    }

    return data;
  }

  /**
   * Adicionar pontos manualmente (Admin)
   */
  static async addManualPoints(
    userId: string,
    organizationId: string,
    points: number,
    description: string,
    adminId: string
  ): Promise<boolean> {
    try {
      // Criar transação
      const { error: transError } = await supabase
        .from('gamification_transactions')
        .insert({
          user_id: userId,
          organization_id: organizationId,
          transaction_type: 'manual_adjustment',
          points_delta: points,
          final_points: points,
          description,
          triggered_by: adminId,
          is_team_transaction: false,
        });

      if (transError) throw transError;

      // Atualizar pontos do usuário
      const currentPoints = await this.getUserPoints(userId, organizationId);
      const newTotal = (currentPoints?.total_points || 0) + points;

      await supabase
        .from('user_points')
        .upsert({
          user_id: userId,
          organization_id: organizationId,
          total_points: newTotal,
          achievement_points: (currentPoints?.achievement_points || 0) + (points > 0 ? points : 0),
          updated_at: new Date().toISOString(),
        });

      return true;
    } catch (error) {
      console.error('Erro ao adicionar pontos manuais:', error);
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // LEADERBOARD
  // --------------------------------------------------------------------------

  /**
   * Obter ranking de uma organização
   */
  static async getLeaderboard(
    organizationId: string,
    limit: number = 50
  ): Promise<LeaderboardEntry[]> {
    const { data, error } = await supabase
      .from('gamification_leaderboard')
      .select('*')
      .eq('organization_id', organizationId)
      .order('total_points', { ascending: false })
      .limit(limit);

    if (error) {
      // gamification_leaderboard view joins auth.users — blocked by RLS (42501)
      // Handled gracefully; fix requires recreating the view to join user_profiles instead
      return [];
    }

    return data || [];
  }

  /**
   * Obter posição de um usuário no ranking
   */
  static async getUserRank(
    userId: string,
    organizationId: string
  ): Promise<number> {
    const { data, error } = await supabase
      .from('gamification_leaderboard')
      .select('rank_position')
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao buscar rank do usuário:', error);
      return 0;
    }

    return data?.rank_position || 0;
  }

  // --------------------------------------------------------------------------
  // TRANSACTIONS
  // --------------------------------------------------------------------------

  /**
   * Obter histórico de transações de um usuário
   */
  static async getUserTransactions(
    userId: string,
    organizationId: string,
    limit: number = 20
  ): Promise<GamificationTransaction[]> {
    const { data, error } = await supabase
      .from('gamification_transactions')
      .select('*')
      .eq('user_id', userId)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao buscar transações:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Obter transações por projeto
   */
  static async getProjectTransactions(
    projectId: string,
    limit: number = 50
  ): Promise<GamificationTransaction[]> {
    const { data, error } = await supabase
      .from('gamification_transactions')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao buscar transações do projeto:', error);
      return [];
    }

    return data || [];
  }

  // --------------------------------------------------------------------------
  // RULES
  // --------------------------------------------------------------------------

  /**
   * Obter regras de gamificação de uma organização
   */
  static async getGamificationRules(
    organizationId: string
  ): Promise<GamificationRules | null> {
    const { data, error } = await supabase
      .from('gamification_rules')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao buscar regras de gamificação:', error);
      return null;
    }

    return data;
  }

  /**
   * Atualizar regras de gamificação
   */
  static async updateGamificationRules(
    organizationId: string,
    updates: Partial<GamificationRules>
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('gamification_rules')
        .upsert({
          organization_id: organizationId,
          ...updates,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao atualizar regras:', error);
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // RECOVERY PLANS
  // --------------------------------------------------------------------------

  /**
   * Criar plano de recuperação
   */
  static async createRecoveryPlan(
    plan: Omit<RecoveryPlan, 'id' | 'opened_at' | 'deadline_at' | 'penalty_stopped' | 'points_recovered'>
  ): Promise<RecoveryPlan | null> {
    try {
      // Calcular deadline (48h)
      const deadline = new Date();
      deadline.setHours(deadline.getHours() + 48);

      const { data, error } = await supabase
        .from('recovery_plans')
        .insert({
          ...plan,
          deadline_at: deadline.toISOString(),
          opened_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar plano de recuperação:', error);
      return null;
    }
  }

  /**
   * Obter planos de recuperação de um Kaizen
   */
  static async getKaizenRecoveryPlans(
    kaizenId: string
  ): Promise<RecoveryPlan[]> {
    const { data, error } = await supabase
      .from('recovery_plans')
      .select('*')
      .eq('kaizen_id', kaizenId)
      .order('opened_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar planos de recuperação:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Atualizar status do plano de recuperação
   */
  static async updateRecoveryPlanStatus(
    planId: string,
    status: RecoveryPlan['status'],
    resolutionNotes?: string
  ): Promise<boolean> {
    try {
      const updates: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'completed' || status === 'failed') {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('recovery_plans')
        .update(updates)
        .eq('id', planId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao atualizar plano de recuperação:', error);
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // UTILITY FUNCTIONS
  // --------------------------------------------------------------------------

  /**
   * Aplicar multiplicador de prazo para um projeto
   */
  static async applyDeadlineMultiplier(
    projectId: string,
    organizationId: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('apply_deadline_multiplier', {
        p_project_id: projectId,
        p_organization_id: organizationId,
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao aplicar multiplicador:', error);
      return false;
    }
  }

  /**
   * Calcular próximo nível
   */
  static getNextLevel(currentLevel: AuthorityLevel): {
    nextLevel: AuthorityLevel | null;
    pointsRequired: number;
  } {
    const levels: Record<AuthorityLevel, { next: AuthorityLevel | null; points: number }> = {
      bronze: { next: 'silver', points: 1000 },
      silver: { next: 'gold', points: 5000 },
      gold: { next: 'black_belt', points: 15000 },
      black_belt: { next: null, points: 0 },
    };

    const config = levels[currentLevel];
    return {
      nextLevel: config.next,
      pointsRequired: config.points,
    };
  }

  /**
   * Verificar se usuário pode abrir plano de recuperação
   */
  static canOpenRecoveryPlan(kaizenStatus: string, existingPlans: RecoveryPlan[]): boolean {
    // Só pode abrir se o Kaizen está regredido
    if (kaizenStatus !== 'regredido') return false;

    // Verificar se já existe um plano aberto
    const openPlan = existingPlans.find(p => p.status === 'open' || p.status === 'in_progress');
    return !openPlan;
  }
}

export default GamificationService;
